import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as cognito from '../cognito';
import { useAuth } from '../AuthContext';
import { applyTheme, getStoredTheme, type ThemeChoice } from '../theme';

const AVATAR_MAX = 1900;

async function fileToSmallDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      const size = 96;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas non disponibile'));
        return;
      }
      ctx.drawImage(img, 0, 0, size, size);
      let q = 0.55;
      let dataUrl = canvas.toDataURL('image/jpeg', q);
      while (dataUrl.length > AVATAR_MAX && q > 0.2) {
        q -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', q);
      }
      if (dataUrl.length > AVATAR_MAX) {
        reject(
          new Error(
            `Immagine troppo grande per Cognito (max ~${AVATAR_MAX} caratteri). Usa un URL https.`
          )
        );
        return;
      }
      resolve(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('File immagine non valido'));
    };
    img.src = url;
  });
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { session, setSession, signOut } = useAuth();
  const [theme, setTheme] = useState<ThemeChoice>(() => getStoredTheme() || 'light');
  const [name, setName] = useState('');
  const [avatarUrlInput, setAvatarUrlInput] = useState('');
  const [avatarStored, setAvatarStored] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const [pwdOld, setPwdOld] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdNew2, setPwdNew2] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdErr, setPwdErr] = useState('');
  const [pwdOk, setPwdOk] = useState('');

  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaErr, setMfaErr] = useState('');
  const [mfaOk, setMfaOk] = useState('');
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  const load = useCallback(async () => {
    if (!session) return;
    setErr('');
    setLoading(true);
    try {
      const p = await cognito.getUserProfile(session.accessToken);
      setEmail(p.email);
      setName(p.name);
      setAvatarStored(p.avatar || '');
      if (p.avatar?.startsWith('http')) setAvatarUrlInput(p.avatar);
      else setAvatarUrlInput('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore caricamento profilo');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshMfa = useCallback(async () => {
    if (!session) return;
    setMfaLoading(true);
    setMfaErr('');
    try {
      const m = await cognito.getUserMfaMethods(session.accessToken);
      setMfaMethods(m);
    } catch (e) {
      setMfaErr(e instanceof Error ? e.message : 'Errore MFA');
    } finally {
      setMfaLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void refreshMfa();
  }, [refreshMfa]);

  async function onStartTotp() {
    if (!session) return;
    setMfaErr('');
    setMfaOk('');
    setMfaBusy(true);
    try {
      const secret = await cognito.associateSoftwareToken(session.accessToken);
      setTotpSecret(secret);
      setTotpCode('');
    } catch (e) {
      setMfaErr(e instanceof Error ? e.message : 'Errore');
    } finally {
      setMfaBusy(false);
    }
  }

  async function onConfirmTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!session || !totpSecret) return;
    setMfaErr('');
    setMfaOk('');
    setMfaBusy(true);
    try {
      await cognito.verifySoftwareToken(session.accessToken, totpCode, 'Spese');
      setTotpSecret(null);
      setTotpCode('');
      setMfaOk('Authenticator app configurata correttamente.');
      await refreshMfa();
    } catch (e) {
      setMfaErr(e instanceof Error ? e.message : 'Codice non valido');
    } finally {
      setMfaBusy(false);
    }
  }

  function onCancelTotp() {
    setTotpSecret(null);
    setTotpCode('');
    setMfaErr('');
  }

  async function onRemoveTotp() {
    if (!session) return;
    if (
      !confirm(
        'Rimuovere l’authenticator (TOTP)? Se l’account richiede MFA, dovrai configurarne uno nuovo al prossimo accesso.'
      )
    ) {
      return;
    }
    setMfaErr('');
    setMfaOk('');
    setMfaBusy(true);
    try {
      await cognito.disassociateSoftwareToken(session.accessToken);
      setMfaOk('Authenticator rimosso.');
      await refreshMfa();
    } catch (e) {
      setMfaErr(e instanceof Error ? e.message : 'Errore');
    } finally {
      setMfaBusy(false);
    }
  }

  function onThemeChange(next: ThemeChoice) {
    setTheme(next);
    applyTheme(next);
  }

  async function onSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    setErr('');
    setOk('');
    setSaving(true);
    try {
      const n = name.trim();
      const url = avatarUrlInput.trim();
      if (url.length > AVATAR_MAX) {
        throw new Error('URL troppo lungo per Cognito.');
      }
      if (url) {
        await cognito.updateUserAttributes(session.accessToken, { name: n, avatar: url });
      } else {
        await cognito.updateUserAttributes(session.accessToken, { name: n });
        if (avatarStored.startsWith('http')) {
          try {
            await cognito.deleteUserAttributes(session.accessToken, ['custom:avatar']);
          } catch {
            /* attributo già assente */
          }
        }
      }
      const refreshed = await cognito.refreshSession(session.refreshToken);
      setSession(refreshed);
      setOk('Profilo aggiornato.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function onRemoveAvatar() {
    if (!session) return;
    setErr('');
    setOk('');
    setSaving(true);
    try {
      await cognito.deleteUserAttributes(session.accessToken, ['custom:avatar']);
      const refreshed = await cognito.refreshSession(session.refreshToken);
      setSession(refreshed);
      setAvatarUrlInput('');
      setOk('Avatar rimosso.');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    } finally {
      setSaving(false);
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !session) return;
    setErr('');
    setOk('');
    try {
      const dataUrl = await fileToSmallDataUrl(file);
      await cognito.updateUserAttributes(session.accessToken, {
        avatar: dataUrl,
        name: name.trim(),
      });
      const refreshed = await cognito.refreshSession(session.refreshToken);
      setSession(refreshed);
      setAvatarUrlInput('');
      setOk('Avatar aggiornato da file.');
      await load();
    } catch (err) {
      setErr(err instanceof Error ? err.message : 'Errore');
    }
  }

  async function onChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    setPwdErr('');
    setPwdOk('');
    if (pwdNew !== pwdNew2) {
      setPwdErr('Le nuove password non coincidono');
      return;
    }
    if (pwdNew.length < 8) {
      setPwdErr('La nuova password deve avere almeno 8 caratteri');
      return;
    }
    setPwdBusy(true);
    try {
      await cognito.changePassword(session.accessToken, pwdOld, pwdNew);
      setPwdOld('');
      setPwdNew('');
      setPwdNew2('');
      setPwdOk('Password aggiornata.');
    } catch (e) {
      setPwdErr(e instanceof Error ? e.message : 'Errore');
    } finally {
      setPwdBusy(false);
    }
  }

  const preview =
    avatarUrlInput.trim().startsWith('http')
      ? avatarUrlInput.trim()
      : avatarStored.startsWith('http') || avatarStored.startsWith('data:')
        ? avatarStored
        : null;

  const hasSoftwareMfa = mfaMethods.includes('SOFTWARE_TOKEN_MFA');
  const hasSmsMfa = mfaMethods.includes('SMS_MFA');
  const totpUri = totpSecret
    ? cognito.buildTotpUri(totpSecret, email.trim() || 'account', 'Spese Famiglia')
    : '';

  return (
    <div className="profile-page">
      <h2 className="page-title">Profilo</h2>

      <section className="profile-card">
        <h3 className="profile-section-title">Aspetto</h3>
        <p className="profile-hint">Il tema è salvato solo su questo dispositivo.</p>
        <div className="theme-toggle">
          <button
            type="button"
            className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
            onClick={() => onThemeChange('light')}
          >
            Chiaro
          </button>
          <button
            type="button"
            className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
            onClick={() => onThemeChange('dark')}
          >
            Scuro
          </button>
        </div>
      </section>

      <section className="profile-card">
        <h3 className="profile-section-title">Movimenti ricorrenti</h3>
        <p className="profile-hint">
          Definisci entrate e uscite fisse mensili; poi inseriscile mese per mese dalla home.
        </p>
        <Link to="/ricorrenti" className="btn-secondary profile-link-btn">
          Gestisci anagrafica
        </Link>
      </section>

      <section className="profile-card">
        <h3 className="profile-section-title">Verifica in due passaggi (MFA)</h3>
        <p className="profile-hint">
          Se MFA è obbligatoria nel pool, serve almeno un metodo (app authenticator o SMS) attivo per
          accedere.
        </p>
        {mfaLoading ? (
          <p className="home-muted">Caricamento stato MFA…</p>
        ) : (
          <>
            {hasSmsMfa && (
              <p className="field-ok" style={{ marginBottom: '0.5rem' }}>
                SMS MFA risulta attivo su questo account.
              </p>
            )}
            {hasSoftwareMfa && !totpSecret && (
              <p className="field-ok" style={{ marginBottom: '0.5rem' }}>
                App authenticator (TOTP) attiva.
              </p>
            )}
            {mfaErr && <p className="field-err">{mfaErr}</p>}
            {mfaOk && <p className="field-ok">{mfaOk}</p>}

            {totpSecret ? (
              <form onSubmit={onConfirmTotp}>
                <p className="profile-hint">
                  Scansiona il QR con Google Authenticator, Microsoft Authenticator o simili, oppure
                  inserisci manualmente la chiave.
                </p>
                {totpUri ? (
                  <div className="mfa-qr-wrap">
                    <img
                      className="mfa-qr"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(totpUri)}`}
                      alt="QR code authenticator"
                      width={180}
                      height={180}
                    />
                  </div>
                ) : null}
                <p className="mfa-secret mono">{totpSecret}</p>
                <div className="field">
                  <label htmlFor="totp-verify">Codice a 6 cifre</label>
                  <input
                    id="totp-verify"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                  />
                </div>
                <button className="btn-primary" type="submit" disabled={mfaBusy}>
                  {mfaBusy ? '…' : 'Conferma e attiva'}
                </button>
                <button type="button" className="btn-secondary" onClick={onCancelTotp} disabled={mfaBusy}>
                  Annulla
                </button>
              </form>
            ) : (
              <div className="mfa-actions">
                {!hasSoftwareMfa && (
                  <button type="button" className="btn-primary" onClick={() => void onStartTotp()} disabled={mfaBusy}>
                    {mfaBusy ? '…' : 'Configura app authenticator'}
                  </button>
                )}
                {hasSoftwareMfa && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => void onRemoveTotp()}
                    disabled={mfaBusy}
                  >
                    Rimuovi app authenticator
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <section className="profile-card">
        <h3 className="profile-section-title">Account</h3>
        {loading ? (
          <p className="home-muted">Caricamento…</p>
        ) : (
          <form onSubmit={onSaveProfile}>
            <div className="field">
              <label>Email</label>
              <input value={email} disabled className="input-disabled" readOnly />
            </div>
            <div className="field">
              <label htmlFor="pname">Nome visualizzato</label>
              <input id="pname" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pavatar">Avatar (URL https)</label>
              <input
                id="pavatar"
                type="url"
                placeholder="https://…"
                value={avatarUrlInput}
                onChange={(e) => setAvatarUrlInput(e.target.value)}
              />
            </div>
            <div className="avatar-row">
              {preview ? (
                <img src={preview} alt="" className="avatar-preview" />
              ) : (
                <div className="avatar-placeholder">{name?.[0]?.toUpperCase() || '?'}</div>
              )}
              <label className="btn-file">
                Scegli immagine
                <input type="file" accept="image/*" className="sr-only" onChange={onPickFile} />
              </label>
              {(avatarStored || avatarUrlInput.trim()) && (
                <button type="button" className="btn-text-danger" onClick={() => void onRemoveAvatar()}>
                  Rimuovi avatar
                </button>
              )}
            </div>
            <p className="profile-hint">
              Per immagini grandi usa un link https. Da file l’immagine viene compressa (limite Cognito ~2KB).
            </p>
            {err && <p className="field-err">{err}</p>}
            {ok && <p className="field-ok">{ok}</p>}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? '…' : 'Salva profilo'}
            </button>
          </form>
        )}
      </section>

      <section className="profile-card">
        <h3 className="profile-section-title">Password</h3>
        <form onSubmit={onChangePassword}>
          <div className="field">
            <label htmlFor="pwd-old">Password attuale</label>
            <input
              id="pwd-old"
              type="password"
              autoComplete="current-password"
              value={pwdOld}
              onChange={(e) => setPwdOld(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pwd-new">Nuova password</label>
            <input
              id="pwd-new"
              type="password"
              autoComplete="new-password"
              value={pwdNew}
              onChange={(e) => setPwdNew(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pwd-new2">Ripeti nuova password</label>
            <input
              id="pwd-new2"
              type="password"
              autoComplete="new-password"
              value={pwdNew2}
              onChange={(e) => setPwdNew2(e.target.value)}
            />
          </div>
          {pwdErr && <p className="field-err">{pwdErr}</p>}
          {pwdOk && <p className="field-ok">{pwdOk}</p>}
          <button className="btn-primary" type="submit" disabled={pwdBusy}>
            {pwdBusy ? '…' : 'Cambia password'}
          </button>
        </form>
      </section>

      <section className="profile-card">
        <h3 className="profile-section-title">Sessione</h3>
        <p className="profile-hint">Esci dall’account su questo dispositivo.</p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            signOut();
            navigate('/login', { replace: true });
          }}
        >
          Esci
        </button>
      </section>
    </div>
  );
}
